'use client';

import { useTranslation } from 'react-i18next';

import { QuickStartPathTask } from '@/components/setup/quick-start/QuickStartPathTask';
import { tierDotClass } from '@/components/setup/quick-start/quickStartHub.utils';
import { Card } from '@/components/ui/card';
import type { QuickStartTaskId, QuickStartTaskResult, QuickStartTier } from '@/lib/api/setup.types';
import { cn } from '@/lib/utils';

type QuickStartTierSectionProps = {
  tier: QuickStartTier;
  tasks: QuickStartTaskResult[];
  /** Required-tier only: the next actionable step, rendered as the "NOW" row. */
  activeTaskId: QuickStartTaskId | null;
  /** Optional helper line under the title; omitted (e.g. for Required) when undefined. */
  subtitle?: string;
  orgSlug: string;
  storeId: string | null;
  helpBase: string;
};

export function QuickStartTierSection({
  tier,
  tasks,
  activeTaskId,
  subtitle,
  orgSlug,
  storeId,
  helpBase,
}: QuickStartTierSectionProps) {
  const { t } = useTranslation('setup');
  if (tasks.length === 0) return null;

  const todo = tasks.filter((task) => task.status === 'pending').length;

  return (
    <Card className="gap-0 py-0">
      <div className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2.5">
            <span className={cn('mt-[7px] size-2 shrink-0 rounded-full', tierDotClass(tier))} />
            <div className="min-w-0">
              <h2 className="text-foreground text-[15px] font-semibold">
                {t(`quickStart.sections.${tier}.title`)}
              </h2>
              {subtitle ? (
                <p className="text-muted-foreground mt-0.5 text-sm">{subtitle}</p>
              ) : null}
            </div>
          </div>
          {todo > 0 ? (
            <span className="mt-0.5 shrink-0 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-orange-700 dark:bg-orange-500/15 dark:text-orange-300">
              {t('quickStart.tierPills.pending', { count: todo })}
            </span>
          ) : null}
        </div>

        <div className="mt-4 space-y-2.5">
          {tasks.map((task) => (
            <QuickStartPathTask
              key={task.id}
              task={task}
              isActive={task.id === activeTaskId}
              orgSlug={orgSlug}
              storeId={storeId}
              helpBase={helpBase}
            />
          ))}
        </div>
      </div>
    </Card>
  );
}
